"use client";

import { openClosableSubscribeDialog } from "@/context/redux/reducers/dialog/subscribe-dialog-slice";
import {
  tableCellStyles,
  tableHeaderStyles,
} from "@/features/analysis/overview/financial-forecast/data/table-style";
import { ForecastPeriod } from "@/features/analysis/overview/financial-forecast/services/financial-forecast-chart";
import { useAppDispatch } from "@/hooks/redux-hooks";
import { Lang } from "@/i18n-config";
import { getTranslations } from "@/utils/i18n/get-translations";

import { Table } from "ui/core/table";
import LockIcon from "ui/icon/shared/LockIcon";

type Props = {
  lang: Lang;
  period: ForecastPeriod;
};

const getHeader = (lang: Lang, period: ForecastPeriod) => {
  const { t } = getTranslations(lang);
  const isQuarter = period === "quarter";

  return [
    isQuarter
      ? t.analysis.overview.forecastTable.quarterly
      : t.analysis.overview.forecastTable.annually,
    t.analysis.overview.forecastTable.eps,
    isQuarter
      ? t.analysis.overview.forecastTable.qoq
      : t.analysis.overview.forecastTable.yoy,
    t.analysis.overview.forecastTable.max,
    t.analysis.overview.forecastTable.min,
  ];
};

const getRows = (period: ForecastPeriod) => {
  const isQuarter = period === "quarter";
  const currentYear = new Date().getFullYear();

  if (isQuarter) {
    const quarters = [
      { year: currentYear, quarter: 1 },
      { year: currentYear, quarter: 2 },
      { year: currentYear, quarter: 3 },
      { year: currentYear, quarter: 4 },
      { year: currentYear + 1, quarter: 1 },
    ];
    return quarters.map(({ year, quarter }) => [
      `${year}Q${quarter}`,
      <LockCell key={`${year}Q${quarter}-eps`} />,
      <LockCell key={`${year}Q${quarter}-qoq`} />,
      <LockCell key={`${year}Q${quarter}-max`} />,
      <LockCell key={`${year}Q${quarter}-min`} />,
    ]);
  } else {
    const years = [
      currentYear - 1,
      currentYear,
      currentYear + 1,
      currentYear + 2,
      currentYear + 3,
    ];
    return years.map((year) => [
      year.toString(),
      <LockCell key={`${year}-eps`} />,
      <LockCell key={`${year}-yoy`} />,
      <LockCell key={`${year}-max`} />,
      <LockCell key={`${year}-min`} />,
    ]);
  }
};

const LockCell = () => {
  const dispatch = useAppDispatch();
  return (
    <div
      className="group flex cursor-pointer justify-center"
      onClick={() => dispatch(openClosableSubscribeDialog())}
    >
      <LockIcon className="w-2.5 text-zinc-500 group-hover:animate-shake" />
    </div>
  );
};

export const MockForecastTable = ({ lang, period }: Props) => {
  const header = getHeader(lang, period);
  const rows = getRows(period);

  return (
    <div className="overflow-x-auto">
      <Table
        data={[header, ...rows]}
        tableHeaderStyles={tableHeaderStyles}
        tableCellStyles={tableCellStyles}
      />
    </div>
  );
};
